import {
	type OrgSegment,
	type RouteScope,
	type RouteSegments,
	type TenantSegment,
	currentRouteScope,
	projectPath,
	resolveRouteScope,
} from '@/lib/routeScope'
import { useAuthStore } from '@/stores/authStore'
import { useLocation, useParams } from '@tanstack/react-router'
import { useMemo } from 'react'

/**
 * Tenancy ids the current URL names, for components.
 *
 * The React counterpart of `parseRouteScope`, and the only place a component should read `orgId` or
 * `tenantId` out of route params: `useParams({ strict: false })` returns whatever segments happen to
 * be there, so every cast to `{ orgId?, tenantId? }` written by hand was another copy of the assumption
 * that a URL segment is an identifier.
 *
 * Migration seam: if routes ever carry organization and project names or slugs, the lookup that turns
 * a segment into the identifier the API wants belongs here and in `parseRouteScope`, not at the
 * hundred call sites.
 */
export function useRouteScope(): RouteScope {
	const { orgId, tenantId } = useRouteSegments()

	// Callers put this in dependency arrays; params is a fresh object on every navigation.
	return useMemo(() => resolveRouteScope({ orgId, tenantId }), [orgId, tenantId])
}

/**
 * The tenancy segments as the URL spells them, for building another URL: a `<Link>`, a `params` object,
 * a path handed to something that is not the router.
 *
 * The counterpart of `useRouteScope`, which hands back identifiers instead. A component that both calls
 * an API and renders links wants each of them, and the two types cannot be swapped by accident.
 */
export function useRouteSegments(): RouteSegments {
	const { orgId, tenantId } = useParams({ strict: false }) as RouteSegments

	return useMemo(() => ({ orgId, tenantId }), [orgId, tenantId])
}

/**
 * Location of the page on screen, with the tenancy ids the URL carries.
 *
 * Distinct from `useRouteContext` in `@/app/layouts/route-context`, which reads the same ids out of a
 * React context: this one also hands back the pathname and search, and works on routes that sit outside
 * the layout provider.
 */
export function useRouteLocation() {
	const location = useLocation()
	const params = useParams({ strict: false })
	const { orgId, tenantId } = useRouteSegments()

	return {
		pathname: location.pathname,
		search: location.search,
		params: params as Record<string, string>,
		tenantId,
		orgId,
	}
}

/**
 * Organization for a call made outside React. URL first, because that is what the request layer
 * derives its tenant header from and the two must not disagree; auth state covers the moment before
 * the URL has caught up with an organization switch.
 */
export function resolveOrganizationId(): string {
	const orgId = resolveOrganizationIdOrEmpty()
	if (!orgId) {
		throw new Error('Organization ID is required')
	}
	return orgId
}

export function resolveOrganizationIdOrEmpty(): string {
	return currentRouteScope().orgId || useAuthStore.getState().currentOrganizationId || ''
}

export function useCurrentOrganizationId(): string | null {
	const { orgId } = useRouteScope()
	const orgIdFromState = useAuthStore(state => state.currentOrganizationId)
	return orgId || orgIdFromState
}

export const DEFAULT_PROJECT_LANDING_SUFFIX = '/device-management/all'

export function getDefaultRouteForUser(tenantId: TenantSegment, orgId?: OrgSegment): string | null {
	if (!orgId) {
		return null
	}
	return projectPath(orgId, tenantId, DEFAULT_PROJECT_LANDING_SUFFIX)
}
